import { Fragment, type ReactNode } from 'react';

/** A parsed block of our constrained Markdown. */
export type MdBlock =
  | { kind: 'heading'; text: string }
  | { kind: 'para'; text: string }
  | { kind: 'list'; items: string[] };

/**
 * Parse the constrained Markdown our AI explanations emit — paragraphs,
 * bold-only lines (section headings), `- ` bullet lists — into a block model.
 * Pure and DOM-free so it is unit-testable; the renderer maps it to JSX.
 * Anything unrecognised becomes a paragraph, so plain-text explanations still read.
 */
export function parseMarkdownLite(text: string): MdBlock[] {
  const lines = text.replace(/\r/g, '').split('\n');
  const blocks: MdBlock[] = [];
  let bullets: string[] = [];
  const flush = () => {
    if (bullets.length) blocks.push({ kind: 'list', items: bullets });
    bullets = [];
  };
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      flush();
      continue;
    }
    const bullet = /^[-*]\s+(.*)/.exec(line);
    if (bullet) {
      bullets.push(bullet[1]!);
      continue;
    }
    flush();
    // ATX (`#`/`##`) or bold-only lines both act as section headings.
    const atx = /^#{1,6}\s+(.*)$/.exec(line);
    const bold = /^\*\*(.+?)\*\*:?$/.exec(line);
    const heading = atx?.[1] ?? bold?.[1];
    if (heading) blocks.push({ kind: 'heading', text: heading.replace(/\*\*/g, '').trim() });
    else blocks.push({ kind: 'para', text: line });
  }
  flush();
  return blocks;
}

/** Render our constrained Markdown as real bullets/headings, styled with tokens. */
export function MarkdownLite({ text, className }: { text: string; className?: string }) {
  const blocks = parseMarkdownLite(text);
  return (
    <div className={className ?? 'space-y-2'}>
      {blocks.map((block, i) => {
        if (block.kind === 'heading') {
          return (
            <p key={i} className="text-xs font-semibold text-bone">
              {block.text}
            </p>
          );
        }
        if (block.kind === 'list') {
          return (
            <ul key={i} className="ml-1 space-y-1">
              {block.items.map((item, j) => (
                <li key={j} className="flex gap-2 text-xs leading-relaxed text-haze">
                  <span aria-hidden className="mt-[0.15rem] text-iris">
                    •
                  </span>
                  <span>{renderInline(item)}</span>
                </li>
              ))}
            </ul>
          );
        }
        return (
          <p key={i} className="text-xs leading-relaxed text-bone">
            {renderInline(block.text)}
          </p>
        );
      })}
    </div>
  );
}

/** A run of inline markdown: plain text, `**bold**`, or `` `code` ``. */
export type InlineToken = { kind: 'text' | 'bold' | 'code'; text: string };

/**
 * Tokenize inline markdown into bold / code / text runs. Code spans win over
 * bold, and backtick content is taken literally (no nested formatting). Pure and
 * DOM-free so it's unit-testable; `renderInline` maps it to JSX.
 */
export function parseInline(text: string): InlineToken[] {
  const tokens: InlineToken[] = [];
  const re = /`([^`]+)`|\*\*([^*]+)\*\*/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) tokens.push({ kind: 'text', text: text.slice(last, m.index) });
    if (m[1] !== undefined) tokens.push({ kind: 'code', text: m[1] });
    else tokens.push({ kind: 'bold', text: m[2]! });
    last = re.lastIndex;
  }
  if (last < text.length) tokens.push({ kind: 'text', text: text.slice(last) });
  return tokens;
}

/** Render inline `**bold**` and `` `code` `` spans; everything else is plain text. */
function renderInline(text: string): ReactNode {
  return parseInline(text).map((t, i) => {
    if (t.kind === 'code') {
      return (
        <code
          key={i}
          className="rounded-sm bg-surface-2 px-1 py-0.5 font-mono text-[0.85em] text-bone"
        >
          {t.text}
        </code>
      );
    }
    if (t.kind === 'bold') {
      return (
        <strong key={i} className="font-semibold text-bone">
          {t.text}
        </strong>
      );
    }
    return <Fragment key={i}>{t.text}</Fragment>;
  });
}

/**
 * Render a single line of inline markdown (`**bold**`, `` `code` ``) as inline
 * nodes — no block wrapper. Use inside list items, table cells, titles, or any
 * span where AI text may contain inline formatting but block layout is unwanted.
 */
export function InlineMarkdown({ text }: { text: string }): ReactNode {
  return <>{renderInline(text)}</>;
}
